<script lang="ts" setup>
import { defaultLocations } from '~/constants/defaultLocations';
import type { Location, ShopItem } from '~/types/Checkout';
import type { OfferBalance } from '~/types/Catalog';

const isShowModal = defineModel<boolean>({ default: false });

const catalogStore = useCatalogStore();
const checkoutStore = useCheckoutStore();

const moskva = defaultLocations[0];
const piter = defaultLocations[1];

const cities = ref<Location[]>([moskva, piter]);
const selectedCity = ref<Location>(cities.value[0]);

watch(
  () => isShowModal.value,
  () => {
    if (!isShowModal.value) checkoutStore.openMarker = null;
  },
);

const layoutTypes = { map: 'map', list: 'list' };
const selectedBlockLayout = ref(layoutTypes.list);

const moscowShops = ref<ShopItem[]>([]);
const piterShops = ref<ShopItem[]>([]);

async function getShops() {
  [moscowShops.value, piterShops.value] = await Promise.all([
    checkoutStore.getShopsForProductCard(moskva.data.kladr_id as string),
    checkoutStore.getShopsForProductCard(piter.data.kladr_id as string),
  ]);
}

getShops();

function setLocationOfAvailableWarehouse() {
  const kladrList: string[] = [];

  catalogStore.selectedOffer.offer_balances.forEach((balance: OfferBalance) => {
    if (
      balance.balance > 0 &&
      balance.warehouse.pickup_store &&
      !kladrList.includes(balance.warehouse.pickup_store.kladr)
    ) {
      kladrList.push(balance.warehouse.pickup_store?.kladr);
    }
  });

  if (kladrList.length) {
    const location = defaultLocations.find((location) => location.data.kladr_id === kladrList[0]);

    if (location) {
      selectedCity.value = location;
    }
  }
}

setLocationOfAvailableWarehouse();

function switchLayout() {
  if (selectedBlockLayout.value === layoutTypes.list) {
    selectedBlockLayout.value = layoutTypes.map;
  } else if (selectedBlockLayout.value === layoutTypes.map) {
    selectedBlockLayout.value = layoutTypes.list;
  }
}

const authStore = useAuthStore();

function getStockInfo(shop: ShopItem) {
  const onlineWarehouse = catalogStore.selectedOffer.offer_balances.find((balance) => balance.warehouse.id === 2);
  const correspondingWarehouse = catalogStore.selectedOffer.offer_balances.find(
    (balance) => balance.warehouse.id === shop.warehouse_id,
  );
  if (!correspondingWarehouse) return '';

  const {
    balance,
    warehouse: { pickup_store },
  } = correspondingWarehouse;

  shop.balance = balance ?? 0;

  if (balance < 1) {
    if (onlineWarehouse && onlineWarehouse.balance > 0 && !pickup_store?.only_stock) {
      return `<span class="text-label-violet">Под заказ, завтра</span>`;
    } else {
      return '<span class="text-hell-black-40">Нет в наличии</span>';
    }
  } else if (balance === 1) {
    return '<span class="text-hell-red">Осталось 1</span>';
  } else if (balance < 5) {
    return `<span class="text-hell-yellow">Осталось ${
      authStore.accessRights?.can_view_balance ? balance : 'мало'
    }</span>`;
  } else {
    return `<span class="text-hell-green">Осталось ${
      authStore.accessRights?.can_view_balance ? balance : 'много'
    }</span>`;
  }
}

function getShopsByCityKladrId(kladrId: string) {
  if (kladrId === moskva.data.kladr_id) return moscowShops.value.sort((a, b) => b.balance - a.balance);
  if (kladrId === piter.data.kladr_id) return piterShops.value.sort((a, b) => b.balance - a.balance);
  return [];
}

function selectCity(kladrId: string) {
  selectedCity.value = defaultLocations.find((location) => location.data.kladr_id === kladrId) as Location;
}

const citiesForBtnGroup = computed(() =>
  cities.value.map((city) => ({
    id: city.data.kladr_id ?? '',
    title: city.data.city ?? '',
    isSelected: selectedCity.value.data.kladr_id === city.data.kladr_id,
  })),
);
</script>

<template>
  <BaseModal
    v-model="isShowModal"
    @close="isShowModal = false"
  >
    <div class="product-page-availability-in-shops-modal">
      <span class="font-bold mt-8 sm:mt-0">Самовывоз из магазинов Hellride</span>

      <SwitchBtnGroup
        :items="citiesForBtnGroup"
        @select-item="selectCity"
      />

      <div class="product-page-availability-in-shops-modal__points">
        <section
          :class="{ '!hidden sm:!flex': selectedBlockLayout === layoutTypes.map }"
          class="product-page-availability-in-shops-modal__shops-list"
        >
          <div
            v-for="shop in getShopsByCityKladrId(selectedCity.data.kladr_id as string)"
            :key="shop.code"
            :class="{
              'product-page-availability-in-shops-modal__shops-list-item--selected':
                checkoutStore.openMarker?.code === shop.code,
            }"
            class="product-page-availability-in-shops-modal__shops-list-item"
            @click="checkoutStore.openMarker = checkoutStore.openMarker?.code === shop.code ? null : shop"
          >
            <div class="text-base font-bold mb-1.5">{{ shop.nearest_station }}</div>
            <div class="font-bold">{{ shop.address }}</div>
            <div>{{ shop.sub_title }}</div>
            <div>{{ shop.work_time_full }}</div>
            <div v-html="getStockInfo(shop)" />
          </div>
        </section>

        <PickupPointsMap
          :class="{ '!hidden sm:!block': selectedBlockLayout === layoutTypes.list }"
          :points="getShopsByCityKladrId(selectedCity.data.kladr_id as string)"
          :selected-location="selectedCity"
          class="md:min-h-96"
        />
      </div>

      <TheBtn
        class="mb-3 sm:!hidden"
        @click="switchLayout"
      >
        {{ selectedBlockLayout === layoutTypes.map ? 'Список' : 'Карта' }}
      </TheBtn>
    </div>
  </BaseModal>
</template>

<style lang="scss">
.product-page-availability-in-shops-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;

  &__points {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    height: 320px;

    @include screen-sm {
      grid-template-columns: 1fr 1fr;
      height: unset;
    }
  }

  &__shops-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
  }

  &__shops-list-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $hell-black-5;
    font-size: $text-sm;
    cursor: pointer;
    transition: all 0.3s;

    &--selected {
      border: 1px solid $hell-black-60;
    }
  }
}
</style>
